<template>
    <div>
    <ValidationProvider
        tag="div"
        :rules="combinedRules"
        :name="vid"
        :label="label || name"
        :mode="mode"
        :model-value="modelValue"
        @update:model-value="handleInput($event)"
        :validate-on-mount="false"
        :validate-on-model-update="validateOnModelUpdate"
        v-slot="{ errors, handleChange }"
    >
        <b-form-group
            :label-for="name || labelText"
            :label="labelText"
            label-class="text-capitalize"
            :class="{
                'mb-0': compactFormat,
                'compact-container': compactFormat && ! narrowInput,
                'narrow-container': narrowInput,
                'push-right': pushRight
            }"
        >
            <template
                v-for="(_, name) in $slots" :key="name"
                v-slot:[name]="slotData"
            >
                <slot :name="name" v-bind="slotData" />
            </template>
            <b-input-group>
                <input
                    v-bind="$attrs"
                    ref="input"
                    :name="name"
                    type="text"
                    @input="onUserInput($event.target.value, handleChange)"
                    @keydown="handleKeydown"
                    @paste="(e) => handlePaste(e, handleChange)"
                    :disabled="$attrs.disabled || textOnly"
                    :class="{
                        'is-invalid': errors.length,
                        'form-control': true,
                        'form-control-plaintext': textOnly,
                        'pe-2': compactFormat,
                        'compact-control': compactFormat,
                        'align-right': alignRight
                    }"
                    :value="isInputActive ? rawInput : displayValue"
                    @focus="onFocus($event)"
                    @blur="isInputActive = false"
                />
                <template v-slot:append v-if="infoMessage || $slots.append">
                    <b-input-group-text v-if="infoMessage" class="px-1 info-append">
                        <b-icon-info-circle
                            v-b-tooltip="{
                                title: infoMessage,
                                html: !!infoMessageIsHtml,
                                trigger: 'hover',
                                placement: 'auto',
                                boundary: 'viewport',
                                container: 'body',
                                teleportTo: 'body',
                                customClass: 'p-info-tooltip',
                                delay: { show: 0, hide: 100 }
                            }"
                        ></b-icon-info-circle>
                    </b-input-group-text>
                    <slot v-else name="append" />
                </template>
                <template
                    v-for="name in nonAppendSlotNames" :key="name"
                    v-slot:[name]="slotData"
                >
                    <slot :name="name" v-bind="slotData" />
                </template>
            </b-input-group>

            <span class="text-danger">{{ errors[0] }}</span>
        </b-form-group>
    </ValidationProvider>
    </div>
</template>
<script>
import debounce from 'lodash/debounce';
import baseInputs from '@/components/mixins/BaseInputs';
import validatedInput from '@/components/mixins/ValidatedInput';
export default {
    name: 'p-number',
    mixins: [baseInputs, validatedInput],
    data: function() {
        return {
            isInputActive: false,
            // While focused, the input is driven by these literal keystrokes rather
            // than the bound model value. That keeps in-progress entries like "23."
            // intact even when the model coerces them to a number (v-model.number)
            // or never writes back (one-way :model-value with an @change/@blur commit
            // handler, e.g. tax overrides).
            rawInput: '',
            //configure for our standard types here to avoid having to duplicate settings on each use
            inputTypes: {
                money: {
                    precision: undefined, //use default (.00 for US)
                    symbol: undefined //use default ($ prefix for US)
                },
                percent: {
                    //keep trailing zeros to keep alignment with other percentages for easy comparison
                    precision: { min: 2, max: 2 },
                    symbol: { suffix: ' %' }
                },
                percentHighPrecision: {
                    //keep trailing zeros to keep alignment with other percentages for easy comparison
                    precision: { min: 3, max: 3 },
                    symbol: { suffix: ' %' }
                },
                decimal: {
                    precision: { min: 0, max: 2 },
                    symbol: {}
                },
                integer: {
                    precision: { min: 0, max: 0 },
                    symbol: {}
                }
            }
        };
    },
    props: {
        // value prop now comes from modelValue via BaseInputs mixin
        debounceWait: {
            type: Number,
            default: 0
        },
        inputType: {
            type: String,
            default: 'decimal'
        },
        addMoneyRules: {
            type: Boolean,
            default: null
        },
        compactFormat: {
            type: Boolean,
            default: false
        },
        narrowInput: {
            type: Boolean,
            default: false
        },
        textOnly: {
            type: Boolean,
            default: false
        },
        leftAlign: {
            type: Boolean,
            default: false
        },
        //push entire input right
        pushRight: {
            type: Boolean,
            default: false
        },
        infoMessageIsHtml: {
            type: Boolean,
            default: false
        },
        validateOnModelUpdate: {
            type: Boolean,
            default: true
        }
    },
    computed: {
        value() { return this.modelValue; },
        displayValue() {
            if (this.value === null || this.value === undefined || this.value === '') return '';
            const num = Number(this.value);
            if (isNaN(num)) return this.value;
            // Integers and decimals display as-is, no formatting
            if (this.inputType === 'integer' || this.inputType === 'decimal') return this.value;
            const prec = this.precision;
            const sym = this.currency || {};
            let minDigits = 0;
            let maxDigits = 2;
            if (prec) {
                minDigits = prec.min ?? 0;
                maxDigits = prec.max ?? 2;
            } else if (this.inputType === 'money') {
                minDigits = 2;
                maxDigits = 2;
            }
            let formatted = num.toLocaleString('en-US', {
                minimumFractionDigits: minDigits,
                maximumFractionDigits: maxDigits
            });
            if (sym.suffix) formatted = formatted + sym.suffix;
            else if (this.inputType === 'money') formatted = '$' + formatted;
            return formatted;
        },
        currency() {
            return this.inputTypes[this.inputType].symbol;
        },
        precision() {
            return this.inputTypes[this.inputType].precision;
        },
        alignRight() {
            //default is to align right. So in order to align left we return false to remove the align right css.
            return !this.leftAlign;
        },
        nonAppendSlotNames() {
            return Object.keys(this.$slots).filter(name => name !== 'append');
        },
        combinedRules() {
            //if there are validation rules we would expect an input type to always have, we can add them here
            //default to add money rules when in money mode.  It can be turned of
            //by explicitly setting addMoneyRules to false
            if (
                this.addMoneyRules === true ||
                (this.addMoneyRules === null && this.inputType === 'money')
            ) {
                //the db should be able to store 18,4.  However, the user would
                //see some weird behavior when going above 16, 3
                //(rounding small decimal places and truncating decimal places when there were 14 digits to left of decimal).
                // We don't have a need to support this at this time.  We'll likely go to 2 decimals in money anyway.
                if (this.rules.length) {
                    return this.rules + '|p-decimal:16,3';
                }
                return 'p-decimal:16,3';
            }
            if (this.inputType === 'integer') {
                if (this.rules.length) {
                    return this.rules + '|p-integer';
                }
                return 'p-integer';
            }
            return this.rules;
        }
    },
    created() {
        if (this.debounceWait > 0) {
            this.handleInput = debounce(this.handleInput, this.debounceWait);
        }
    },
    methods: {
        focus: function() {
            this.$refs.input?.focus?.() || this.$refs.input?.$el?.focus();
        },
        onFocus(event) {
            // Switch the display to the raw, editable value (not the formatted
            // displayValue) and seed rawInput from the current model value.
            this.isInputActive = true;
            this.rawInput =
                this.value === null || this.value === undefined ? '' : String(this.value);
            event.target.select();
        },
        onUserInput(val, handleChange) {
            // Normal typing is already constrained to numeric characters by
            // handleKeydown, so for typed input this sanitize is a no-op. But
            // browser autofill (and any programmatic fill) fires `input` directly,
            // bypassing handleKeydown, and can inject a formatted value like
            // "$37.00" or "5 %". Number() then yields NaN and the model becomes
            // null (invalid). Strip it the same way pasted text is cleaned so the
            // value parses. Keep the on-screen text in sync with the cleaned value.
            const cleaned = this.sanitizeNumeric(val);
            this.rawInput = cleaned;
            handleChange(cleaned);
        },
        // Strip everything that isn't a digit, a single decimal point, or a single
        // leading minus, respecting the integer input type. Shared by the typed/
        // autofill (onUserInput) and paste (handlePaste) paths.
        sanitizeNumeric(text) {
            const allowed = this.inputType === 'integer' ? /[^0-9-]/g : /[^0-9.-]/g;
            let cleaned = (text ?? '').replace(allowed, '');
            const isNegative = cleaned.startsWith('-');
            cleaned = cleaned.replace(/-/g, '');
            const parts = cleaned.split('.');
            cleaned = parts.shift() + (parts.length ? '.' + parts.join('') : '');
            if (isNegative) cleaned = '-' + cleaned;
            return cleaned;
        },
        // Emit a clean numeric model value (a number, or null when empty/invalid)
        // so consumers never receive a NaN-producing string like '' or '.'. This
        // overrides BaseInputs.handleInput. The literal on-screen text is preserved
        // separately via rawInput, so in-progress entries such as "23." still show
        // while typing.
        toModelValue(value) {
            if (value === '' || value === null || value === undefined) return null;
            const n = Number(value);
            return Number.isFinite(n) ? n : null;
        },
        handleInput(value) {
            const cleaned = this.toModelValue(value);
            this.$emit('update:modelValue', cleaned);
            this.$emit('input', cleaned);
        },
        handleKeydown(event) {
            // Let modifier combos (copy/paste/cut/select-all/undo/etc.) and
            // function keys through untouched.
            if (event.ctrlKey || event.metaKey || event.altKey) return;
            if (/^F\d{1,2}$/.test(event.key)) return;

            // Always allow navigation / editing control keys.
            const controlKeys = [
                'Backspace', 'Delete', 'Tab', 'Enter', 'Escape',
                'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
                'Home', 'End'
            ];
            if (controlKeys.includes(event.key)) return;

            // Digits 0-9 always allowed.
            if (/^[0-9]$/.test(event.key)) return;

            const target = event.target;
            const value = target.value ?? '';
            const selStart = target.selectionStart ?? 0;
            const selEnd = target.selectionEnd ?? 0;
            const selectionCoversAll = selStart === 0 && selEnd === value.length;

            // Negative sign — only at the very start, and only one. A full
            // selection replaces the value, so '-' becomes the new content.
            if (event.key === '-') {
                if (selectionCoversAll) return;
                if (selStart === 0 && !value.includes('-')) return;
                event.preventDefault();
                return;
            }

            // Decimal point — disallowed for integer types, and only one.
            if (event.key === '.' && this.inputType !== 'integer') {
                if (selectionCoversAll) return;
                if (!value.includes('.')) return;
                event.preventDefault();
                return;
            }

            // Anything else (letters, symbols) is rejected.
            event.preventDefault();
        },
        handlePaste(event, handleChange) {
            // Strip everything that isn't a digit, decimal, or leading minus
            // from pasted text so users can paste "$30,000.50" and get 30000.50.
            const pasted = (event.clipboardData || window.clipboardData)?.getData('text') ?? '';
            const cleaned = this.sanitizeNumeric(pasted);

            if (cleaned === pasted) return; // nothing to scrub — let the default paste happen.
            event.preventDefault();

            const target = event.target;
            const start = target.selectionStart ?? 0;
            const end = target.selectionEnd ?? 0;
            const before = (target.value ?? '').slice(0, start);
            const after = (target.value ?? '').slice(end);
            const newValue = before + cleaned + after;
            // Keep the focused display in sync with the scrubbed paste.
            if (this.isInputActive) {
                this.rawInput = newValue;
            }
            // Route through the field's handleChange (when available) so the
            // paste is validated, mirroring the @input path. Falls back to a
            // plain emit for any non-validated usage.
            if (handleChange) {
                handleChange(newValue);
            } else {
                this.handleInput(newValue);
            }
            // Restore the caret position after Vue patches the input value.
            this.$nextTick(() => {
                const pos = before.length + cleaned.length;
                target.setSelectionRange?.(pos, pos);
            });
        }
    }
};
</script>
<style scoped lang="scss">
:deep(input.align-right ) {
    text-align: right;
}
:deep(.form-control-plaintext ) {
    border: none;
    padding: 0 10px;
    margin: 0;
    background: none;
}
:deep(.compact-control ) {
    padding-bottom: 5px;
    padding-top: 8px;
    padding-left: 2px;
    height: 1.5rem;
    width: 85px;
}

:deep(.compact-container ) {
    width: 88px;
}

:deep(.narrow-container ) {
    width: 55px;
}

:deep(.push-right ) {
    margin-left: auto;
}

:deep(input.form-control[disabled]),
:deep(input.form-control:disabled) {
    background-color: #edf1f2;
}

/* Info-icon append: drop the grey input-group-text box so the icon floats
   cleanly next to the field. */
:deep(.info-append) {
    background-color: transparent;
    border: none;
}

</style>

<!-- Unscoped: tooltip is teleported outside the component, so the styling
     can't live in scoped CSS. Widen info tooltips and stack above modals. -->
<style lang="scss">
.tooltip.p-info-tooltip,
.p-info-tooltip {
    max-width: 360px !important;
    z-index: 2000 !important;
}
.p-info-tooltip .tooltip-inner {
    max-width: 360px !important;
    text-align: left !important;
    white-space: normal !important;
}
</style>
